import {Component, Input, Output, EventEmitter, ViewEncapsulation} from '@angular/core';
import {CmlTemplateRowComponent} from '../cml-template-row/cml-template-row.component';
import {CmlGroupSectionComponent} from '../cml-group-section/cml-group-section.component';
import {TplSource, TplConfig, TplGroup, TemplateGroup, SourceBlock, allSelected, someSelected} from '../cml-types';

@Component({
  selector: 'cml-source-section',
  standalone: true,
  imports: [CmlTemplateRowComponent, CmlGroupSectionComponent],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="blocSource" [class.bsOn]="expanded">
      <div class="hbs" (click)="toggleExpand.emit()">
        <div class="contcheckSrc">
          <input type="checkbox" class="inCheckSrc" [id]="'src' + eventId + source.type"
                 [checked]="isAllChecked()"
                 [indeterminate]="isIndeterminate()"
                 (click)="$event.stopPropagation()"
                 (change)="toggleAll.emit(source.templates)">
          <label class="labCheckSrc" [for]="'src' + eventId + source.type" (click)="$event.stopPropagation()">
            <i class="fas fa-check icCheckSrc"></i>
          </label>
        </div>
        <div class="nameSrc"><i [class]="source.icon"></i> &nbsp; {{ source.label }}</div>
        <div class="checkTplSrc">
          <div class="bulcheckTplSrc" [class.bulcs2]="checkedCount() > 0">
            <span class="qtchecksrc">{{ checkedCount() }}</span>
            <span> / {{ source.templates.length }}</span>
          </div>
        </div>
        <!-- Toujours rendus (visibility gérée en CSS) pour que bulcheckTplSrc garde la même position panel ouvert ou fermé -->
        <div class="hinfoColor"></div>
        <div class="hinfoNfc"></div>
        <div class="hinfoNbScan"></div>
      </div>
      <div class="bbs">
        @if (source.type === 'online') {
          @for (group of getGroups(); track group._id ?? 'default') {
            <cml-group-section
              [group]="group"
              [eventId]="eventId"
              [configs]="configs"
              [searchTerm]="searchTerm"
              (toggleAll)="toggleAll.emit($event)"
              (tplToggle)="tplToggle.emit($event)"
              (tplNfcChange)="tplNfcChange.emit($event)"
              (tplScanChange)="tplScanChange.emit($event)"
              (tplColorChange)="tplColorChange.emit($event)"
            />
          }
        } @else {
          @for (tpl of source.templates; track tpl._id; let idx = $index) {
            <cml-template-row
              [tpl]="tpl"
              [config]="configs[tpl._id]"
              [searchTerm]="searchTerm"
              (toggle)="tplToggle.emit($event)"
              (nfcChange)="tplNfcChange.emit($event)"
              (scanChange)="tplScanChange.emit($event)"
              (colorChange)="tplColorChange.emit($event)"
            />
          }
        }
      </div>
    </div>
  `
})
export class CmlSourceSectionComponent {
  @Input() source!: SourceBlock;
  @Input() eventId!: string;
  @Input() expanded = false;
  @Input() configs: Record<string, TplConfig> = {};
  @Input() searchTerm = '';
  @Input() tplGroups: TplGroup[] = [];
  @Output() toggleExpand = new EventEmitter<void>();
  @Output() toggleAll = new EventEmitter<TplSource[]>();
  @Output() tplToggle = new EventEmitter<string>();
  @Output() tplNfcChange = new EventEmitter<{id: string; value: string}>();
  @Output() tplScanChange = new EventEmitter<{id: string; value: number}>();
  @Output() tplColorChange = new EventEmitter<{id: string; value: string}>();

  checkedCount(): number {
    return this.source.templates.filter(t => this.configs[t._id]?.checked).length;
  }

  isAllChecked(): boolean {
    return allSelected(this.checkedCount(), this.source.templates.length);
  }

  isIndeterminate(): boolean {
    return someSelected(this.checkedCount(), this.source.templates.length);
  }

  getGroups(): TemplateGroup[] {
    const groupMap = new Map<string, TplSource[]>();
    const defaultGroup: TplSource[] = [];

    for (const tpl of this.source.templates) {
      const gId = tpl.idGroup || tpl.idTplGroup;
      if (gId && gId !== 'no-group' && gId !== 'none') {
        if (!groupMap.has(gId)) groupMap.set(gId, []);
        groupMap.get(gId)!.push(tpl);
      } else {
        defaultGroup.push(tpl);
      }
    }

    for (const templates of groupMap.values()) {
      templates.sort((a, b) => (a.sortInGroup ?? 0) - (b.sortInGroup ?? 0));
    }
    defaultGroup.sort((a, b) => (a.sortInGroup ?? 0) - (b.sortInGroup ?? 0));

    const groups: TemplateGroup[] = this.tplGroups
      .filter(g => groupMap.has(g._id))
      .sort((a, b) => a.sort - b.sort)
      .map(g => ({
        _id: g._id,
        name: g.name,
        color: g.color,
        isDefault: false,
        templates: groupMap.get(g._id) || []
      }));

    // Orphan templates (idTplGroup points to a deleted group) fall into default
    for (const [gId, templates] of groupMap) {
      if (!this.tplGroups.some(g => g._id === gId)) {
        defaultGroup.push(...templates);
      }
    }

    groups.push({
      _id: null,
      name: 'Groupe par défaut',
      color: 'neutral',
      isDefault: true,
      templates: defaultGroup
    });

    return groups;
  }
}
